import React, { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import BreadcrumbsComponent from "../components/Breadcrumbs";
import { Box, Button, CircularProgress, Chip } from "@mui/material";
import { routes } from "../routes/routes";
import DataTableGeneric from "../components/DataTableGeneric";
import AddIcon from "@mui/icons-material/Add";
import { ErrorSnackbar } from "../components/Snackbar/ErrorSnackbar";
import { SuccessSnackbar } from "../components/Snackbar/SuccessSnackbar";
import { useNavigate } from "react-router-dom";
import SearchInput from "../components/SearchInput";
import useFetchAllQuestionnaires from "../hooks/useFetchAllQuestionnaires";
import useAssociateInstrumentQuestionnaire from "../hooks/useAssociateInstrumentQuestionnaire";
import EditInstrumentAssociationModal from "../components/Modal/EditInstrumentAssociationModal";
import useBuildQuestionnaireJson from "../hooks/useBuildQuestionnaireJson";
import useSaveQuestionnaireJson from "../hooks/useSaveQuestionnaireJson";
import BuildJsonModal from "../components/Modal/BuildJsonModal";

// Columnas para la tabla de cuestionarios
const questionnairesColumns = [
  { id: "id", label: "ID", width: "80px" },
  { id: "externalId", label: "ID Externo", width: "120px" },
  { id: "title", label: "Título del Cuestionario" },
  { id: "type", label: "Tipo", width: "100px" },
  { id: "applications", label: "Aplicaciones asociadas" },
  { id: "instruments", label: "Instrumentos asociados" },
  { id: "actions", label: "Acciones" },
];

const QuestionnairesPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const { data: allQuestionnaires, loading: loadingQuestionnaires, error: questionnairesError } = useFetchAllQuestionnaires();
  const [questionnaires, setQuestionnaires] = useState([]);
  const { associateInstrument, data: associateData, error: associateError, loading: associateLoading } = useAssociateInstrumentQuestionnaire();
  const { buildJson, data: jsonData, loading: buildingJson, error: buildJsonError } = useBuildQuestionnaireJson();
  const { saveJson, data: saveData, loading: savingJson, error: saveJsonError } = useSaveQuestionnaireJson();

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showInstrumentModal, setShowInstrumentModal] = useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);
  const [showBuildJsonModal, setShowBuildJsonModal] = useState(false);

  const handleShowInstrumentModal = (questionnaire) => {
    setSelectedQuestionnaire(questionnaire);
    setShowInstrumentModal(true);
  };

  const handleCloseInstrumentModal = () => {
    setShowInstrumentModal(false);
    setSelectedQuestionnaire(null);
  };

  const handleShowBuildJsonModal = (questionnaire) => {
    setSelectedQuestionnaire(questionnaire);
    setShowBuildJsonModal(true);
    buildJson(questionnaire.id).catch((err) => {
      setShowError(true);
      setErrorMessage(err?.message || "Error al construir el JSON del cuestionario.");
    });
  };

  const handleCloseBuildJsonModal = () => {
    setShowBuildJsonModal(false);
    setSelectedQuestionnaire(null);
  };

  const handleSaveJsonToMongo = (questionnaireId, jsonData) => {
    saveJson(questionnaireId, jsonData)
      .then(() => {
        setShowSuccess(true);
        setSuccessMessage("JSON guardado exitosamente en MongoDB.");
        handleCloseBuildJsonModal();
      })
      .catch((err) => {
        console.error("Error saving questionnaire JSON:", err);
        setShowError(true);
        setErrorMessage(err?.message || "Error al guardar el JSON en MongoDB.");
      });
  };

  const handleAssociateInstrument = async (instrument) => {
    if (!selectedQuestionnaire) return;

    const result = await associateInstrument(instrument.id, { questionnaire_id: selectedQuestionnaire.id });
    if (!result) {
      setShowError(true);
      setErrorMessage(associateError?.message || "Error al asociar el instrumento al cuestionario.");
      return;
    }

    // Actualizar la lista de cuestionarios con el nuevo instrumento
    setQuestionnaires((prevQuestionnaires) =>
      prevQuestionnaires.map((q) =>
        q.id === selectedQuestionnaire.id
          ? {
              ...q,
              instruments: [...(q.instruments || []), instrument],
            }
          : q,
      ),
    );

    setSelectedQuestionnaire((prevQuestionnaire) => ({
      ...prevQuestionnaire,
      instruments: [...(prevQuestionnaire.instruments || []), instrument],
    }));

    setShowSuccess(true);
    setSuccessMessage("Cuestionario asociado exitosamente al instrumento.");
    handleCloseInstrumentModal();
  };

  useEffect(() => {
    if (questionnairesError) {
      setErrorMessage(questionnairesError?.message || "Error al cargar los cuestionarios.");
      setShowError(true);
    }
  }, [questionnairesError]);

  useEffect(() => {
    setQuestionnaires(allQuestionnaires || []);
  }, [allQuestionnaires]);

  // Filas de cuestionarios
  const questionnairesRows = (questionnaires || [])
    .filter((questionnaireItem) => {
      const search = searchTerm.trim().toLowerCase();

      const byTitle = (questionnaireItem.title || "").trim().toLowerCase().includes(search);

      const byExternalId = (questionnaireItem.external_id ? String(questionnaireItem.external_id).trim().toLowerCase() : "").includes(search);

      return byTitle || byExternalId;
    })
    .map((questionnaireItem) => {
      const apps = Array.isArray(questionnaireItem.applications) ? questionnaireItem.applications : [];
      const instruments = Array.isArray(questionnaireItem.instruments) ? questionnaireItem.instruments : [];

      return {
        id: questionnaireItem.id,
        externalId: questionnaireItem.external_id,
        title: questionnaireItem.title?.trim() || "[Sin título]",
        type: questionnaireItem.questionnaire_type,
        applications: apps.length ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {apps.map((app) => (
              <Chip key={app.id} size="small" label={app.name} />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            [No hay aplicaciones asociadas]
          </Typography>
        ),
        instruments: instruments.length ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {instruments.map((instrument) => (
              <Chip key={instrument.id} size="small" label={instrument.name} />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            [No hay instrumentos asociados]
          </Typography>
        ),
        actions: [
          { buttonText: "Vista previa", onClick: () => navigate(routes.cuestionarioPreview.replace(":id", questionnaireItem.id)) },
          { buttonText: "Editar instrumento", onClick: () => handleShowInstrumentModal(questionnaireItem) },
          { buttonText: "Construir", onClick: () => handleShowBuildJsonModal(questionnaireItem) },
        ],
      };
    });

  return (
    <div className="min-w-full p-10 overflow-x-auto">
      <BreadcrumbsComponent
        breadcrumbs={[
          <Link underline="none" key="1" color="inherit" href={routes.root}>
            Página Principal
          </Link>,
          <Typography key="2" color="secondary" sx={{ fontSize: "14px" }}>
            Cuestionarios
          </Typography>,
        ]}
      />

      {/* Encabezado */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 4,
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: "#333333" }}>
          Cuestionarios
        </Typography>
        <Box gap={4} sx={{ display: "flex", alignItems: "center" }}>
          <Button variant="outlined" onClick={() => navigate(routes.cuestionarioCrear)} startIcon={<AddIcon />} sx={{ height: 40 }}>
            Crear Cuestionario
          </Button>
        </Box>
      </Box>

      {/* Buscador */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ mb: 3 }}>
          <SearchInput
            id="search"
            placeholder={"Buscar cuestionarios por ID externo o título..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Box>

        {/* Tabla */}
        {loadingQuestionnaires ? (
          <div className="flex justify-center items-center h-40">
            <CircularProgress />
          </div>
        ) : (
          <DataTableGeneric columns={questionnairesColumns} rows={questionnairesRows} />
        )}
      </Box>

      {/* Snackbars */}
      <ErrorSnackbar open={showError} onClose={() => setShowError(false)} title="Ha ocurrido un error" message={errorMessage} />
      <SuccessSnackbar open={showSuccess} onClose={() => setShowSuccess(false)} title="Operación exitosa" message={successMessage} />

      {/* Modales */}
      <EditInstrumentAssociationModal
        showModal={showInstrumentModal}
        handleCloseModal={handleCloseInstrumentModal}
        value={selectedQuestionnaire}
        onSubmitAssociate={handleAssociateInstrument}
      />
      <BuildJsonModal
        open={showBuildJsonModal}
        onClose={handleCloseBuildJsonModal}
        jsonData={jsonData}
        loading={buildingJson}
        questionnaireId={selectedQuestionnaire?.id}
        onSave={handleSaveJsonToMongo}
        savingJson={savingJson}
      />
    </div>
  );
};

export default QuestionnairesPage;
